import { getItem, isChoiceAvailable, stockMax, usableChoices } from "../data/catalog";

export const CART_STORAGE_KEY = "mrMarketplaceCart";
const LEGACY_STORAGE_KEY = "inquiryCartV2";

// A cart line is keyed by product id:
//   items without choices: { id, qty, addedAt }
//   items with choices:    { id, items: { [choice]: qty }, selectionAddedAt: { [choice]: ts }, addedAt }

export function linePieces(line) {
  if (!line) return 0;
  if (line.items) return Object.values(line.items).reduce((sum, n) => sum + (Number(n) || 0), 0);
  return Number(line.qty) || 0;
}

export function cartCount(cart) {
  return Object.values(cart).reduce((sum, line) => sum + linePieces(line), 0);
}

export function selectionQty(cart, id, name) {
  return cart[id]?.items?.[name] || 0;
}

function parseJson(raw) {
  try {
    return JSON.parse(raw || "null");
  } catch {
    return null;
  }
}

// Old builds saved one entry per add, keyed by product+options. Fold them into one line per product.
function fromLegacy(legacy) {
  const cart = {};
  Object.values(legacy || {}).forEach((entry) => {
    const item = entry && getItem(entry.id);
    if (!item) return;
    const hasChoices = usableChoices(item).length > 0;
    const line = cart[item.id] || (hasChoices ? { id: item.id, items: {}, selectionAddedAt: {}, addedAt: 0 } : { id: item.id, qty: 0, addedAt: 0 });
    const addedAt = Number(entry.addedAt) || 0;
    if (hasChoices) {
      const picks = String(entry.opts?.selections || "").split(" + ").map((s) => s.trim()).filter(Boolean);
      const map = entry.bundleItems && Object.keys(entry.bundleItems).length
        ? entry.bundleItems
        : picks.reduce((m, name) => ({ ...m, [name]: (m[name] || 0) + Math.max(1, Number(entry.qty) || 1) }), {});
      Object.entries(map).forEach(([name, qty]) => {
        line.items[name] = (line.items[name] || 0) + (Number(qty) || 0);
        line.selectionAddedAt[name] = Math.max(line.selectionAddedAt[name] || 0, addedAt);
      });
    } else {
      const pieces = entry.autoPricing ? Number(entry.pieceCount) || 1 : Number(entry.qty) || 1;
      line.qty += pieces;
    }
    line.addedAt = Math.max(line.addedAt, addedAt);
    cart[item.id] = line;
  });
  return cart;
}

// Drop unknown products/choices and clamp to stock so a stale save can never exceed availability.
function sanitize(cart) {
  const clean = {};
  Object.values(cart || {}).forEach((line) => {
    const item = line && getItem(line.id);
    if (!item) return;
    if (item.saleUnit === "set" && line.saleUnit !== "set") return;
    const max = stockMax(item);
    if (!max) return;
    if (usableChoices(item).length) {
      const choices = usableChoices(item);
      const items = {};
      let remaining = item.stockQuantity ?? Infinity;
      Object.entries(line.items || {}).forEach(([name, qty]) => {
        const n = Math.min(max, remaining, Math.floor(Number(qty) || 0));
        if (choices.includes(name) && isChoiceAvailable(item, name) && n > 0) { items[name] = n; remaining -= n; }
      });
      if (Object.keys(items).length) clean[item.id] = { ...line, id: item.id, items, selectionAddedAt: line.selectionAddedAt || {} };
    } else {
      const qty = Math.min(max, Math.floor(Number(line.qty) || 0));
      if (qty > 0) clean[item.id] = { id: item.id, qty, addedAt: Number(line.addedAt) || 0, ...(item.saleUnit ? { saleUnit: item.saleUnit } : {}) };
    }
  });
  return clean;
}

export function loadCart() {
  const stored = parseJson(localStorage.getItem(CART_STORAGE_KEY));
  if (stored) return sanitize(stored);
  return sanitize(fromLegacy(parseJson(localStorage.getItem(LEGACY_STORAGE_KEY))));
}

export function saveCart(cart) {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch {
    // Storage can be unavailable (private mode); the cart simply stays in memory.
  }
}

export function cartReducer(cart, action) {
  const now = Date.now();
  switch (action.type) {
    case "setQuantity": {
      const { item } = action;
      if (!stockMax(item)) return cart;
      const qty = Math.max(1, Math.min(stockMax(item), Math.floor(action.qty) || 1));
      return { ...cart, [item.id]: { id: item.id, qty, addedAt: now, ...(item.saleUnit ? { saleUnit: item.saleUnit } : {}) } };
    }
    case "addSelection": {
      const { item, name } = action;
      if (!usableChoices(item).includes(name) || !isChoiceAvailable(item, name)) return cart;
      const line = cart[item.id] || { id: item.id, items: {}, selectionAddedAt: {} };
      const current = line.items[name] || 0;
      const remaining = item.stockQuantity != null ? Math.max(0, item.stockQuantity - linePieces(line)) : Infinity;
      const next = Math.min(stockMax(item), current + Math.min(remaining, Math.max(1, Math.floor(action.amount) || 1)));
      if (next === line.items[name]) return cart;
      return {
        ...cart,
        [item.id]: {
          ...line,
          items: { ...line.items, [name]: next },
          selectionAddedAt: { ...line.selectionAddedAt, [name]: now },
          addedAt: now,
        },
      };
    }
    case "removeSelection": {
      const line = cart[action.id];
      if (!line?.items) return cart;
      const items = { ...line.items };
      delete items[action.name];
      const rest = { ...cart };
      if (Object.keys(items).length) rest[action.id] = { ...line, items };
      else delete rest[action.id];
      return rest;
    }
    case "remove": {
      const rest = { ...cart };
      delete rest[action.id];
      return rest;
    }
    default:
      return cart;
  }
}
